import { useMemo, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { GridColDef, GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid"
import { isUndefined } from "lodash"

import Table from "@/components/table/Table"
import Selector from "@/components/form/selector/Selector"
import Pagination from "@/components/table/Pagination"
import {
  useGroupPlayersQuery,
  usePlayerGroupsQuery,
  usePlayersQuery,
} from "@/hooks/api/useSettingQuery"
import { useUpsertPlayerGroupMutation } from "@/hooks/api/usePlayerQuery"
import { PlayerSetting } from "@/types/setting"
import { createGlobalStore } from "@/stores/global"
import { usePositionRank } from "@/hooks/usePositionRank"
import { PAGE_SIZE } from "@/constants/pagination"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"

import "./playerGroupTable.scss"

const UNIFORM_NUMBER_WIDTH = 119
const NAME_MAX_WIDTH = 770
const POSITION_WIDTH = 102
const GROUP_WIDTH = 250

type GroupOptionType = {
  value: string
  name: string
  label: string
}

export default function PlayerGroupTable({
  idfPlayerGroup,
  searchText,
  isLoading = false,
}: {
  searchText: string
  idfPlayerGroup?: number
  isLoading?: boolean
}) {
  const { t } = useTranslation()
  const positionRank = usePositionRank()
  const [rows, setRows] = useState<PlayerSetting[]>([])
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  })

  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const { data: allPlayers } = usePlayersQuery()
  const { data: groupPlayers } = useGroupPlayersQuery(idfPlayerGroup)
  const { data: groups } = usePlayerGroupsQuery()

  const changeGroupMutation = useUpsertPlayerGroupMutation()

  useEffect(() => {
    const newRows = isUndefined(idfPlayerGroup) && allPlayers ? allPlayers : (groupPlayers ?? [])

    const filteredNewRows = newRows.filter((player) => {
      if (!searchText || searchText.length === 0) {
        return true
      }
      const searchLower = searchText.toLowerCase()

      const lowerNickname = player.nickname?.toLowerCase() || ""
      const lowerPosition = player.position?.toLowerCase() || ""
      const uniformNumberStr = String(player.uniformNumber ?? "")

      const playerGroup = groups?.teamPlayerGroups.find(
        (g) => g.idfTeamPlayerGroup === player.idfPlayerGroup,
      )
      const lowerGroupName = playerGroup?.name?.toLowerCase() || ""

      return (
        lowerNickname.includes(searchLower) ||
        lowerPosition.includes(searchLower) ||
        uniformNumberStr.includes(searchText) ||
        lowerGroupName.includes(searchLower)
      )
    })

    setRows(filteredNewRows)
  }, [idfPlayerGroup, allPlayers, groupPlayers, searchText, groups])

  const handleChangeGroup = useCallback(
    async (
      _: unknown,
      { value }: GroupOptionType,
      params: GridRenderCellParams<PlayerSetting, any, any, GridTreeNodeWithRender>,
    ) => {
      try {
        const newValue = value === "none" ? null : Number(value)

        await changeGroupMutation.mutateAsync({
          idfPlayer: String(params.row.idfPlayer),
          payload: { idfPlayerGroup: newValue },
        })

        setSnackBar({
          message: t("messages.successUpdate"),
          severity: "success",
          open: true,
        })
      } catch (e) {
        console.error(e)
      }
    },
    [changeGroupMutation, setSnackBar, t],
  )

  const columns = useMemo<GridColDef<PlayerSetting>[]>(() => {
    if (!groups) {
      return []
    }

    const groupOptions: GroupOptionType[] = [
      {
        value: "none",
        name: `(${t("none")})`,
        label: `(${t("none")})`,
      },
      ...groups.teamPlayerGroups.map((group) => ({
        value: String(group.idfTeamPlayerGroup),
        name: group.name,
        label: group.name,
      })),
    ]

    return [
      {
        field: "uniformNumber",
        renderHeader: () => t("uniformNumber"),
        width: UNIFORM_NUMBER_WIDTH,
      },
      {
        field: "nickname",
        renderHeader: () => t("name"),
        flex: 1,
        maxWidth: NAME_MAX_WIDTH,
      },
      {
        field: "position",
        renderHeader: () => t("position"),
        width: POSITION_WIDTH,
        sortComparator: (v1, v2) => positionRank(v2) - positionRank(v1),
      },
      {
        field: "idfPlayerGroup",
        renderHeader: () => t("group"),
        width: GROUP_WIDTH,
        renderCell: (params) => {
          const currentValue =
            params.row.idfPlayerGroup === null || isUndefined(params.row.idfPlayerGroup)
              ? "none"
              : String(params.row.idfPlayerGroup)

          return (
            <Selector
              options={groupOptions}
              value={groupOptions.find((option) => option.value === currentValue)}
              onChange={(_: unknown, cell: GroupOptionType) => handleChangeGroup(_, cell, params)}
              renderOption={({ key, ...optionProps }, option) => {
                return (
                  <li key={key} {...optionProps}>
                    {option.value === "none" ? `(${t("none")})` : option.name}{" "}
                    {currentValue === option.value && (
                      <Icon type="check" color={colors.neutral.grey[1000]} />
                    )}
                  </li>
                )
              }}
              width={235}
            />
          )
        },
      },
    ]
  }, [groups, t, handleChangeGroup, positionRank])

  if (columns.length === 0) {
    return <div />
  }

  return (
    <Table
      className="player-group-table"
      columns={columns}
      rows={rows}
      disableColumnResize
      getRowId={(row) => row.idfPlayer}
      rowHeight={52}
      disableAutosize={false}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 10 },
        },
        sorting: { sortModel: [{ field: "uniformNumber", sort: "asc" }] },
      }}
      pagination
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      slots={{
        pagination: Pagination,
      }}
      slotProps={{
        cell: {
          title: "",
        },
      }}
      loading={isLoading}
    />
  )
}
